import { useEffect, useMemo, useState } from "react";
import { api, MARKET_ORDER, MARKET_META } from "../lib/api";
import { Sparkles, Info } from "lucide-react";
import { useSeo } from "../lib/useSeo";

const SEO = {
    common_number: {
        title: "Shillong Morning Teer Common Number Today | Daily Predictions",
        description: "Today's Shillong Morning Teer Common Number predictions across all markets. Curated shortlists based on trending analysis — for informational purposes only.",
        keywords: "shillong morning teer common number, teer common number today, morning teer prediction",
        path: "/common-number",
    },
    dream_number: {
        title: "Shillong Morning Teer Dream Number Today | Dream Chart",
        description: "Shillong Morning Teer Dream Number chart — interpret your dreams into likely Teer numbers. Updated daily for all Meghalaya markets.",
        keywords: "shillong morning teer dream number, teer dream chart, morning teer dream",
        path: "/dream-number",
    },
    hit_guti: {
        title: "Shillong Morning Teer Direct Hit Guti Today | Strong Numbers",
        description: "Direct Hit Guti for Shillong Morning Teer — today's strongest predicted numbers picked by our analysts. Refreshed every session.",
        keywords: "shillong morning teer direct hit guti, hit guti today, morning teer strong number",
        path: "/hit-guti",
    },
};

const TYPES = {
    common_number: {
        title: "Common Number",
        subtitle: "Aaj ke top predicted numbers har market ke liye",
        color: "from-amber-500 to-yellow-400",
    },
    dream_number: {
        title: "Dream Number",
        subtitle: "Sapno ke hisaab se guti nikaalne ki common list",
        color: "from-fuchsia-500 to-pink-400",
    },
    hit_guti: {
        title: "Direct Hit Guti",
        subtitle: "Aaj ki sabse strong direct hit guti",
        color: "from-emerald-500 to-teal-400",
    },
};

const Predictions = ({ type }) => {
    const meta = TYPES[type];
    const seoMeta = SEO[type];
    useSeo({
        ...seoMeta,
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: meta.title, path: seoMeta.path },
        ],
    });

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            if (type === "common_number") {
                // Auto common numbers — one call fetches all markets
                const { data } = await api.get("/common-number/today");
                setItems(Array.isArray(data) ? data : []);
            } else {
                const { data } = await api.get("/predictions/today", { params: { type } });
                setItems(data);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [type]);

    const byMarket = useMemo(() => Object.fromEntries(items.map(i => [i.market_key, i])), [items]);

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-10" data-testid={`predictions-${type}`}>
            <div className={`rounded-2xl bg-gradient-to-r ${meta.color} text-white p-8 md:p-10 relative overflow-hidden`}>
                <div className="grain-overlay opacity-10" />
                <div className="relative">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-xs font-semibold uppercase tracking-widest">
                        <Sparkles className="h-3.5 w-3.5" /> Today
                    </div>
                    <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight mt-3">{meta.title}</h1>
                    <p className="mt-2 text-white/90 max-w-xl">{meta.subtitle}</p>
                </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {loading && MARKET_ORDER.map((k) => (
                    <div key={k} className="card-crisp h-40 skeleton" />
                ))}
                {!loading && MARKET_ORDER.map((k) => {
                    const meta2 = MARKET_META[k];
                    const p = byMarket[k];
                    const nums = p?.numbers || [];
                    return (
                        <div key={k} className="card-crisp p-5" data-testid={`pred-card-${k}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: meta2.accent }} />
                                    <div className="font-display font-bold text-blue-950 tracking-tight">{meta2.name}</div>
                                </div>
                                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                                    {p ? "Updated" : "Awaiting"}
                                </div>
                            </div>

                            {(() => {
                                const isCommon = type === "common_number";
                                if (isCommon) {
                                    const dmy = p?.date
                                        ? (() => { const [y,m,d]=p.date.split("-"); return `${d}/${m}/${y}`; })()
                                        : (() => {
                                            const iso = new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
                                            const [y,m,d] = iso.split("-");
                                            return `${d}/${m}/${y}`;
                                          })();
                                    const shortLabel = meta2.name.replace(/ Teer$/i, "").toUpperCase();
                                    return (
                                        <div
                                            className="mt-3 rounded-md border border-slate-200 overflow-hidden bg-white"
                                            style={{ borderTop: `3px solid ${meta2.accent}` }}
                                        >
                                            <div className="px-2 py-1 text-center font-num font-bold text-slate-900 text-xs" data-testid={`pred-date-${k}`}>
                                                {dmy}
                                            </div>
                                            <div className="px-2 py-0.5 text-center bg-slate-100 border-y border-slate-200 font-display font-bold uppercase text-slate-900 text-[11px] tracking-wider">
                                                {shortLabel}
                                            </div>
                                            <div className="grid grid-cols-2 divide-x divide-slate-300">
                                                {[
                                                    { label: "F/R", direct: p?.fr_direct, house: p?.fr_house, ending: p?.fr_ending },
                                                    { label: "S/R", direct: p?.sr_direct, house: p?.sr_house, ending: p?.sr_ending },
                                                ].map((row, idx) => (
                                                    <div key={idx}>
                                                        <div className="text-center py-0.5 text-[9px] font-semibold text-slate-500 border-b border-slate-200 bg-slate-50">
                                                            {row.label}
                                                        </div>
                                                        <div className="grid grid-cols-3 bg-emerald-300/80 border-b border-slate-200">
                                                            <div className="py-0.5 text-center text-slate-900 border-r border-slate-200 text-[9px]">Direct</div>
                                                            <div className="py-0.5 text-center text-slate-900 border-r border-slate-200 text-[9px]">House</div>
                                                            <div className="py-0.5 text-center text-slate-900 text-[9px]">Ending</div>
                                                        </div>
                                                        <div className="grid grid-cols-3">
                                                            <div className="py-1 text-center border-r border-slate-200 result-digit text-sm text-slate-900" data-testid={`pred-direct-${row.label.toLowerCase().replace('/','')}-${k}`}>
                                                                {row.direct && row.direct.length ? row.direct.join(",") : "—"}
                                                            </div>
                                                            <div className="py-1 text-center border-r border-slate-200 result-digit text-sm text-slate-900" data-testid={`pred-house-${row.label.toLowerCase().replace('/','')}-${k}`}>
                                                                {row.house || "—"}
                                                            </div>
                                                            <div className="py-1 text-center result-digit text-sm text-slate-900" data-testid={`pred-ending-${row.label.toLowerCase().replace('/','')}-${k}`}>
                                                                {row.ending || "—"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                                return nums.length === 0 ? (
                                    <div className="mt-5 flex items-center gap-2 text-sm text-slate-400">
                                        <Info className="h-4 w-4" /> Aaj ka {meta.title.toLowerCase()} abhi upload nahi hua.
                                    </div>
                                ) : (
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {nums.map((n, i) => (
                                            <div
                                                key={i}
                                                className="min-w-[52px] px-3 py-2 rounded-lg bg-blue-50 border border-blue-100 text-blue-950 result-digit text-xl text-center"
                                                data-testid={`pred-num-${k}-${i}`}
                                            >
                                                {String(n).padStart(2, "0")}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                            {p?.notes && (
                                <div className="mt-4 text-xs text-slate-500 border-t pt-3 border-slate-100">
                                    {p.notes}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="mt-10 rounded-xl bg-yellow-50 border border-yellow-200 p-5 text-sm text-yellow-900 flex gap-3">
                <Info className="h-5 w-5 shrink-0 mt-0.5" />
                <p>
                    Disclaimer: Ye predictions sirf analysis ke basis par diye jaate hain — koi guarantee nahi.
                    Please local laws ka palan karein.
                </p>
            </div>
        </div>
    );
};

export default Predictions;
